import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, ClipboardCheck, MessageCircle, Package, Smartphone, UserRound } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import './ProvisionWizard.css';
import { AppLicenseNotice } from './PartnerAppPicker';

const STEPS = ['client', 'plan', 'app', 'review'];

export function useProvisionWizard({ open, issues, onError, lastStep = 3 }) {
  const [step, setStep] = useState(0);
  useEffect(() => { if (open) setStep(0); }, [open]);
  function goTo(target) {
    if (!Number.isInteger(target) || target < 0 || target > lastStep) return;
    const invalid = issues.findIndex((message, index) => index < target && message);
    if (invalid >= 0) {
      setStep(invalid);
      onError(issues[invalid]);
      return;
    }
    onError('');
    setStep(target);
  }
  return { step, goTo, reset: () => setStep(0) };
}

export function ProvisionSteps({ step, onChange, disabled, steps }) {
  const { t } = useLanguage();
  const items = steps || STEPS.map(key => ({ label: t(`pw.${key}`) }));
  return <nav className="prov-steps" aria-label={t('pw.steps')}>
    <ol style={{ '--provision-step-count': items.length }}>{items.map(({ label }, index) => <li key={index}>
      <button type="button" aria-current={step === index ? 'step' : undefined}
        aria-label={t('pw.stepName', { n: index + 1, name: label })}
        disabled={disabled} onClick={() => onChange(index)}>
        <span className="prov-step-number" aria-hidden="true">{index < step ? <Check size={14} /> : index + 1}</span>
        <span>{label}</span>
      </button>
    </li>)}</ol>
    <p>{t('pw.progressCount', { n: step + 1, count: items.length })}<span className="prov-step-hint"> · {steps ? steps[step]?.hint : t(`pw.hint${step}`)}</span></p>
  </nav>;
}

export function ProvisionStep({ active, index, title: stepTitle, children }) {
  const { t } = useLanguage();
  const title = useRef(null);
  useEffect(() => {
    if (!active) return;
    const body = title.current?.closest('.prov-body');
    if (body) body.scrollTop = 0;
    title.current?.focus({ preventScroll: true });
  }, [active]);
  if (!active) return null;
  return <div className="prov-step-panel">
    <h3 ref={title} tabIndex={-1} className="sr-only">{stepTitle || t(`pw.${STEPS[index]}`)}</h3>
    {children}
  </div>;
}

export function ProvisionNavigation({ step, onChange, disabled, onCancel, children, lastStep = 3 }) {
  const { t } = useLanguage();
  return <div className="prov-step-actions">
    <Button type="button" variant="outline" disabled={disabled}
      onClick={() => step === 0 ? onCancel() : onChange(step - 1)}>
      {step > 0 && <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />}
      {t(step === 0 ? 'pw.cancel' : 'pw.back')}
    </Button>
    {step < lastStep ? <Button key="provision-advance" type="button" disabled={disabled} onClick={(event) => {
      // The browser must not submit if React replaces this button with the final action during this click.
      event.preventDefault();
      onChange(step + 1);
    }}>
      {t(step === lastStep - 1 ? 'pw.reviewAction' : 'pw.next')}
      <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
    </Button> : <React.Fragment key="provision-confirm">{children}</React.Fragment>}
  </div>;
}

/** Shared review for entry points with credentials, billing or delivery sections. Never pass a password. */
export function ProvisionReviewSections({ sections, onEdit, disabled, children }) {
  const { t } = useLanguage();
  return <div className="prov-review prov-review--sections">
    <div className="prov-review-intro"><ClipboardCheck aria-hidden="true" size={20} /><div>
      <h3>{t('pw.reviewTitle')}</h3><p>{t('pw.reviewHint')}</p>
    </div></div>
    {sections.map(({ title, fields, step }) => <section key={title} className="prov-review-card">
      <div className="prov-review-head"><h4>{title}</h4>
        <button type="button" disabled={disabled} onClick={() => onEdit(step)}
          aria-label={t('pw.editSection', { name: title })}>{t('pw.edit')}</button>
      </div>
      <dl>{fields.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value ?? '—'}</dd></div>)}</dl>
    </section>)}
    {children && <div className="prov-review-delivery">{children}</div>}
  </div>;
}

export function ProvisionReview({ name, phone, planName, planDetails, appName, app, sendAuto, onEdit, disabled }) {
  const { t } = useLanguage();
  const cards = [
    { key: 'client', icon: UserRound, fields: [[t('cc.clientName'), name], [t('cc.whatsapp'), phone]], step: 0 },
    { key: 'plan', icon: Package, fields: [[t('pw.plan'), planName], ...planDetails], step: 1 },
    { key: 'app', icon: Smartphone, fields: [[t('pw.app'), appName]], step: 2 },
  ];
  return <div className="prov-review">
    <div className="prov-review-intro"><ClipboardCheck aria-hidden="true" size={20} /><div>
      <h3>{t('pw.reviewTitle')}</h3><p>{t('pw.reviewHint')}</p>
    </div></div>
    {cards.map(({ key, icon: Icon, fields, step }) => <section key={key} className={`prov-review-card prov-review-card--${key}`}>
      <div className="prov-review-head"><Icon size={18} aria-hidden="true" /><h4>{t(`pw.${key}`)}</h4>
        <button type="button" disabled={disabled} onClick={() => onEdit(step)}
          aria-label={t('pw.editSection', { name: t(`pw.${key}`) })}>{t('pw.edit')}</button>
      </div>
      <dl>{fields.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value || '—'}</dd></div>)}</dl>
      {key === 'app' && <AppLicenseNotice app={app} />}
    </section>)}
    <div className="prov-review-delivery"><MessageCircle size={20} aria-hidden="true" /><div>
      <strong>{t(sendAuto ? 'pw.autoOn' : 'pw.autoOff')}</strong>
      <p>{t(sendAuto ? 'pw.autoOnHint' : 'pw.autoOffHint')}</p>
    </div><button type="button" disabled={disabled} onClick={() => onEdit(0)}>{t('pw.edit')}</button></div>
  </div>;
}
